
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Loader2, Save, Plus, Settings, ArrowDown, Info } from 'lucide-react';
import { useCommunityFeatures, usePlanFeatures, useManagePlanFeatures } from '@/hooks/useFeatures';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';

interface PlanFeaturesManagerProps {
  planId: string;
  planName: string;
  communityId: string;
}

const PlanFeaturesManager = ({ planId, planName, communityId }: PlanFeaturesManagerProps) => {
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  const { data: communityFeatures, isLoading: featuresLoading } = useCommunityFeatures(communityId);
  const { data: planFeatures, isLoading: planFeaturesLoading } = usePlanFeatures(planId, communityId);
  const { mutate: updatePlanFeatures, isPending: updating } = useManagePlanFeatures();

  // Set initial selected features when plan features load (only direct features)
  React.useEffect(() => {
    if (planFeatures && planFeatures.directFeatures) {
      setSelectedFeatureIds(planFeatures.directFeatures.map(f => f.id));
    }
  }, [planFeatures]);

  const handleFeatureToggle = (featureId: string, checked: boolean) => {
    setSelectedFeatureIds(prev => 
      checked 
        ? [...prev, featureId]
        : prev.filter(id => id !== featureId)
    );
  };

  const handleSave = () => {
    updatePlanFeatures(
      { planId, featureIds: selectedFeatureIds, communityId },
      {
        onSuccess: () => {
          setIsDialogOpen(false);
        }
      }
    );
  };

  const directFeatures = planFeatures?.directFeatures || [];
  const inheritedFromPlans = planFeatures?.inheritedFromPlans || [];
  const totalFeatureCount = directFeatures.length + inheritedFromPlans.length;

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Settings className="h-4 w-4" />
          Manage Features
          <Badge variant="secondary" className="ml-1">
            {totalFeatureCount}
          </Badge>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Manage Features for {planName}
          </DialogTitle>
          <DialogDescription>
            Select the features that should be directly assigned to this plan. 
            Higher-ranked plans automatically inherit features from lower-ranked plans.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {featuresLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Loading features...</span>
            </div>
          ) : (
            <>
              {communityFeatures && communityFeatures.length > 0 ? (
                <div className="space-y-4">
                  {/* Direct Features Section */}
                  <div>
                    <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                      Direct Features
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-4 w-4 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Features specifically assigned to this plan</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </h3>
                    <div className="space-y-3">
                      {communityFeatures.map((feature) => {
                        const isSelected = selectedFeatureIds.includes(feature.id);
                        const isInherited = inheritedFromPlans.length > 0 && directFeatures.some(f => f.feature_key === feature.feature_key);
                        
                        return (
                          <Card key={feature.id} className={`transition-colors ${isSelected ? 'bg-primary/5 border-primary/20' : ''} ${isInherited ? 'opacity-60' : ''}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start space-x-3">
                                <Checkbox
                                  id={feature.id}
                                  checked={isSelected}
                                  onCheckedChange={(checked) => 
                                    handleFeatureToggle(feature.id, checked as boolean)
                                  }
                                  className="mt-1"
                                  disabled={isInherited}
                                />
                                <div className="flex-1">
                                  <label 
                                    htmlFor={feature.id}
                                    className={`font-medium cursor-pointer ${isInherited ? 'text-muted-foreground' : ''}`}
                                  >
                                    {feature.name}
                                    {isInherited && (
                                      <Badge variant="secondary" className="ml-2 text-xs">
                                        Inherited
                                      </Badge>
                                    )}
                                  </label>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {feature.description}
                                  </p>
                                  <div className="mt-2">
                                    <Badge variant="outline" className="text-xs">
                                      {feature.feature_key}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>

                  {/* Inherited Features Section */}
                  {inheritedFromPlans.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                          <ArrowDown className="h-4 w-4 text-green-600" />
                          Inherited Features
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="h-4 w-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Features automatically inherited from lower-ranked plans</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </h3>
                        <div className="text-sm text-green-700 mb-3">
                          Features inherited from: {inheritedFromPlans.join(', ')}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-6 text-center">
                    <Plus className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">
                      No features have been created for this community yet.
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {selectedFeatureIds.length} direct feature(s) selected
              {inheritedFromPlans.length > 0 && (
                <span className="text-green-600 ml-2">
                  + {inheritedFromPlans.length} inherited plan(s)
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={updating}
                className="gap-2"
              >
                {updating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Features
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanFeaturesManager;
